import { SmartCalcErrors } from './helpers';

export const smartCalculation = (
  orders: any[] = [],
  justProducedTotal: number,
): [any[], SmartCalcErrors | null] => {
  const [totalOrdered = 0, totalDistributed = 0] = orders?.reduce(
    ([accOrdered, accDistributed], curr: any) => {
      return [accOrdered + (curr?.amount ?? 0), accDistributed + (curr?.distributed ?? 0)];
    },
    [0, 0],
  );

  // EQUAL
  if (+totalOrdered - totalDistributed - justProducedTotal === 0) {
    return [
      orders.map((el: any) => ({
        ...el,
        justProduced: +(el.amount ?? 0) - (el.distributed ?? 0),
      })),
      null,
    ];
  }

  // LESS
  if (+totalOrdered - totalDistributed - justProducedTotal > 0) {
    const { arrayDONE, array100, arrayRest, ordered100, orderedRest } = getMINDataReduced(orders);
    if (ordered100 + orderedRest <= justProducedTotal) {
      // Can satisfy all minimums
      return [
        [
          ...arrayDONE,
          ...array100,
          ...subtractByCycleMIN(arrayRest, totalOrdered - justProducedTotal - totalDistributed),
        ],
        null,
      ];
    } else if (ordered100 && ordered100 <= justProducedTotal) {
      // Can satisfy minimums for priority customers
      return [[...arrayDONE, ...array100, ...arrayRest], SmartCalcErrors.ONLY_PRIORITY];
    } else {
      // Cannot satisfy any customer
      return [orders, SmartCalcErrors.ALL];
    }
  }

  // MORE
  if (+totalOrdered - totalDistributed - justProducedTotal < 0) {
    const { arrayDONE, array100, arrayRest, ordered100, orderedRest } = getMAXDataReduced(orders);

    if (ordered100 + orderedRest < justProducedTotal) {
      // excceded the limit
      return [
        [
          ...arrayDONE,
          ...array100,
          ...subtractByCycleMAX(arrayRest, orderedRest + ordered100 - totalOrdered),
        ],
        SmartCalcErrors.LIMIT,
      ];
    } else {
      // within brackets
      return [
        [
          ...arrayDONE,
          ...array100,
          ...subtractByCycleMAX(arrayRest, totalOrdered - justProducedTotal - totalDistributed),
        ],
        null,
      ];
    }
  }

  return [orders, SmartCalcErrors.COMMON];
};

const getMINDataReduced = (orders: any[]) => {
  return orders.reduce(
    (acc, curr) => {
      if (curr?.productStatus === 'DONE' || curr?.productStatus === 'Done') {
        return {
          ...acc,
          arrayDONE: [...acc.arrayDONE, { ...curr, justProduced: 0 }],
        };
      } else if (curr?.distributionLowerLimitInPercent >= 100) {
        return {
          ...acc,
          array100: [...acc.array100, { ...curr, justProduced: curr.amount }],
          ordered100: acc.ordered100 + getMINAmount(curr),
        };
      } else {
        return {
          ...acc,
          arrayRest: [...acc.arrayRest, curr],
          orderedRest: acc.orderedRest + getMINAmount(curr),
        };
      }
    },
    {
      arrayDONE: [],
      array100: [],
      arrayRest: [],
      ordered100: 0,
      orderedRest: 0,
    },
  );
};

const getMAXDataReduced = (orders: any[]) => {
  return orders.reduce(
    (acc, curr) => {
      if (curr?.productStatus === 'DONE' || curr?.productStatus === 'Done') {
        return {
          ...acc,
          arrayDONE: [...acc.arrayDONE, { ...curr, justProduced: 0 }],
        };
      } else if (curr?.distributionHigherLimitInPercent <= 100) {
        return {
          ...acc,
          array100: [
            ...acc.array100,
            { ...curr, justProduced: Number((curr.amount ?? 0) - (curr.distributed ?? 0)) },
          ],
          ordered100:
            (acc.ordered100 ?? 0) +
            (Math.floor(curr.amount * (curr.distributionHigherLimitInPercent / 100)) ?? 0) -
            (curr.distributed ?? 0),
        };
      } else {
        return {
          ...acc,
          arrayRest: [
            ...acc.arrayRest,
            {
              ...curr,
              justProduced: Number((curr.amount ?? 0) - (curr.distributed ?? 0)),
            },
          ],
          orderedRest:
            (acc.orderedRest ?? 0) +
            (Math.floor(curr.amount * (curr.distributionHigherLimitInPercent / 100)) ?? 0) -
            (curr.distributed ?? 0),
        };
      }
    },
    {
      arrayDONE: [],
      array100: [],
      arrayRest: [],
      ordered100: 0,
      orderedRest: 0,
    },
  );
};

// The least an order may receive in a shortage (its lower deviation limit). The same rule
// decides whether the minimums can be met and how far each order may be cut.
const getMINAmount = (order: any) =>
  Math.ceil(order?.amount * (order?.distributionLowerLimitInPercent / 100));

const subtractByCycleMIN = (orders: any[], iterationsNumber: number) => {
  const res = JSON.parse(JSON.stringify(orders))
    ?.sort((a: any, b: any) => {
      if (a.amount - getMINAmount(a) > b.amount - getMINAmount(b)) {
        return -1;
      } else if (a.amount - getMINAmount(a) < b.amount - getMINAmount(b)) {
        return 1;
      }
      return a.amount - b.amount;
    })
    ?.map((el: any) => ({ ...el, justProduced: el.amount }));
  let limit = Math.ceil(Math.abs(iterationsNumber));
  for (let i = 0; i < limit; i++) {
    if (limit > iterationsNumber + 9999) {
      limit = 0; // infinite loop preventing
    }
    const currentItem = res[i % res.length];
    // cut one more only if the order stays within its lower deviation limit
    const canSubtract = currentItem?.justProduced - 1 >= getMINAmount(currentItem);
    if (canSubtract) {
      currentItem.justProduced = currentItem.justProduced - 1;
    } else {
      limit++;
    }
  }
  // 0 is a valid result for an order that may be cut completely, not "no value"
  return res;
};

const subtractByCycleMAX = (orders: any[], iterationsNumber: number) => {
  const res = JSON.parse(JSON.stringify(orders))
    ?.sort((a: any, b: any) => {
      if (
        Math.floor(a.amount * (a.distributionHigherLimitInPercent / 100)) - a.amount >
        Math.floor(b.amount * (b.distributionHigherLimitInPercent / 100)) - b.amount
      ) {
        return -1;
      } else if (
        Math.floor(a.amount * (a.distributionHigherLimitInPercent / 100)) - a.amount <
        Math.floor(b.amount * (b.distributionHigherLimitInPercent / 100)) - b.amount
      ) {
        return 1;
      }
      return a.amount - b.amount;
    })
    ?.map((el: any) => ({ ...el, justProduced: (el.amount ?? 0) - (el.distributed ?? 0) }));

  let limit = Math.ceil(Math.abs(iterationsNumber));
  for (let i = 0; i < limit; i++) {
    if (limit > iterationsNumber + 9999) {
      limit = 0; // infinite loop preventing
    }
    const currentItem = res[i % res.length];
    const canSubtract =
      Math.floor(currentItem?.amount * (currentItem?.distributionHigherLimitInPercent / 100)) >=
      currentItem?.justProduced;
    if (canSubtract) {
      currentItem.justProduced = currentItem?.justProduced + 1;
    } else {
      limit++;
    }
  }
  return res;
};
